import { effect, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { catchError, EMPTY, map, Observable, take, tap } from 'rxjs';
import { OmniSearchResult } from '@brighthub/search';
import {
  ClientSessionService,
  SingleUiSessionsResponse,
  UiSession,
  UiSessionsResponse,
} from '@brighthub/uisession';
import { ApiService, GenericResponseCode } from '@brighthub/api';

@Injectable({
  providedIn: 'root'
})
export class ConversationService implements OnDestroy {
  private _uiSessionService = inject(ClientSessionService);
  activeConversation = signal<UiSession | null>(null);
  conversationHistory = signal<UiSession[] | []>([]);
  private _tabId = crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  private _channel = new BroadcastChannel('brightai-conversations');

  constructor() {
  
    effect(() => {
      const history = this.conversationHistory();
      try {
        this._channel.postMessage({
          type: 'updateConversationHistory',
          payload: history,
          meta: { sender: this._tabId, ts: Date.now() }
        });
      } catch (err) {
        console.warn('Failed to post conversationHistory', err);
      }
    });

    this._channel.onmessage = (event: MessageEvent) => {
      const { type, payload, meta } = event.data ?? {};
      if (!type || !payload) return;
      // ignore our own messages (prevents echo/ping-pong)
      if (meta?.sender === this._tabId) return;

      switch (type) {
        case 'updateConversationHistory':
          // merge instead of a blind set:
          this._applyRemoteHistory(payload as UiSession[], meta?.ts);
          break;
      }
    };
  }

  private _applyRemoteHistory(remote: UiSession[], _ts?: number) {
    const current = this.conversationHistory();
    const byId = new Map(current.map(s => [s.conversationId, s]));

    const merged: UiSession[] = [];
    for (const s of remote) {
      if (byId.has(s.conversationId)) {
        merged.push(byId.get(s.conversationId)!);
      } else {
        merged.push(s);
      }
    }

    for (const s of current) {
      if (!merged.some(m => m.conversationId === s.conversationId)) {
        merged.push(s);
      }
    }

    this.conversationHistory.set(merged);
  }

  queryConversationById(conversationId: string): Observable<OmniSearchResult<UiSession>[]> {
    return this._uiSessionService.getUiSession<SingleUiSessionsResponse>(`/conversation/${conversationId}`)
      .pipe(map(res => {
        if (res.result.code === GenericResponseCode.SERVICE_OK) {
          const results =  {description: res.payload.uiSession.uiSessionId, title: res.payload.uiSession.conversationId!, data: res.payload.uiSession};

          return [results];
        }
        return [];
      }))
  }

  getConversationById(conversationId: string) {
    this._uiSessionService.getUiSession<SingleUiSessionsResponse>(`/conversation/${conversationId}`)
      .pipe(
        take(1),
        tap((res) => {
          if (res.result.code === GenericResponseCode.SERVICE_OK) {
            const session = res.payload.uiSession;
            this.setActiveConversation(session);
          } else {
            // do something here
          }
        }),
        catchError((err) => {
          return EMPTY;
        })
      ).subscribe();
  }

  setActiveConversation(session: UiSession) {
    this.activeConversation.set(session);
    this._addToHistory(session);
  }

  private _addToHistory(session: UiSession) {
    const current = this.conversationHistory();
    const exists = current.some(c => c.conversationId === session.conversationId);

    if (!exists) {
      this.conversationHistory.set([session, ...current]);
    }
  }

  clearActiveConversation() {
    this.activeConversation.set(null);
  }

  ngOnDestroy(): void {
    this._channel.close();
  }
}
